/* One instance per section. Collection HTML is cached only for the current page. */
(() => {
  if (customElements.get('kj-collection-atlas')) return;
  let wishlistSequence = 0;

  class CollectionAtlas extends HTMLElement {
    connectedCallback() {
      if (this.life) return;
      this.life = new AbortController();
      const options = { signal: this.life.signal };
      this.parents = [...this.querySelectorAll('[data-parent]')];
      this.groups = [...this.querySelectorAll('[data-group]')];
      this.categories = [...this.querySelectorAll('[data-category]')];
      this.results = this.querySelector('[data-results]');
      this.status = this.querySelector('[data-status]');
      this.error = this.querySelector('[data-error]');
      this.cache = this.sharedCache || new Map();
      this.requestNumber = (this.requestNumber || 0) + 1;
      this.active = this.categories.find(tab => tab.dataset.category === this.dataset.activeChild);
      const renderedURL = this.results.querySelector('[data-atlas-products]')?.dataset.collectionUrl;
      const matchesActive = this.active && renderedURL && new URL(renderedURL, location.href).href === this.active.href;
      if (matchesActive) this.cache.set(this.active.href, this.cleanMarkup(this.results));

      this.querySelector('[data-parents]').setAttribute('role', 'tablist');
      this.parents.forEach(tab => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', `KAGroup-${this.dataset.sectionId}-${tab.dataset.parent}`);
        tab.setAttribute('aria-selected', String(tab.dataset.parent === this.dataset.activeParent));
        tab.tabIndex = tab.dataset.parent === this.dataset.activeParent ? 0 : -1;
      });
      this.groups.forEach(group => {
        group.setAttribute('role', 'tabpanel');
        group.setAttribute('aria-labelledby', `KAParent-${this.dataset.sectionId}-${group.dataset.group}`);
      });
      this.categories.forEach(tab => {
        tab.setAttribute('aria-controls', this.results.id);
        tab.setAttribute('aria-current', tab === this.active ? 'true' : 'false');
      });
      this.addEventListener('click', event => this.handleClick(event), options);
      this.addEventListener('keydown', event => this.handleKey(event), options);
      this.addEventListener('scroll', event => {
        if (event.target === this.track && !this.frame) {
          this.frame = requestAnimationFrame(() => { this.frame = null; this.updateCarousel(); });
        }
      }, { ...options, capture: true, passive: true });
      document.addEventListener('wishlist-hero-links-binded', () => this.initWishlist(), options);
      document.addEventListener('wishlist-hero-wishlist-sdk-ready', () => this.initWishlist(), options);
      document.addEventListener('shopify:block:select', event => {
        const id = event.detail?.blockId;
        const parent = this.parents.find(tab => tab.dataset.parent === id);
        const category = this.categories.find(tab => tab.dataset.category === id);
        if (this.progressive && category) {
          const group = category.closest('[data-group]');
          const controller = this.ensureParent(group.dataset.group);
          controller?.selectCategory(category);
          controller?.querySelector('.ka-panel')?.scrollIntoView({ block: 'start' });
          return;
        }
        if (parent) this.selectParent(parent);
        else if (category) {
          const group = category.closest('[data-group]');
          this.showParent(this.parents.find(tab => tab.dataset.parent === group.dataset.group));
          this.selectCategory(category);
        }
      }, options);
      this.resize = new ResizeObserver(() => this.updateCarousel());
      this.resize.observe(this);
      this.bindProducts();
      // An editor reconnect can interrupt a fetch while the old collection is still rendered.
      if (this.active && !matchesActive) this.selectCategory(this.active, true);
      if (this.dataset.scrollParents === 'true' && this.active) this.startProgressive();
    }

    disconnectedCallback() {
      this.requestNumber++;
      this.stopProgressive();
      this.life?.abort();
      this.pending?.abort();
      clearTimeout(this.timeout);
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.life = null;
    }

    cleanMarkup(root) {
      const panel = root.querySelector('[data-atlas-products]')?.cloneNode(true);
      if (!panel) return '';
      panel.querySelectorAll('.ka-wishlist-button').forEach(el => {
        el.replaceChildren();
        el.className = 'wishlist-hero-custom-button ka-wishlist-button';
        delete el.dataset.kaWishlist;
      });
      panel.querySelector('[data-dots]')?.replaceChildren();
      return panel.outerHTML;
    }

    handleClick(event) {
      if (event.target.closest('kj-collection-atlas') !== this) return;
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const parent = event.target.closest('[data-parent]');
      const category = event.target.closest('[data-category]');
      if (parent) { event.preventDefault(); this.selectParent(parent); }
      else if (category) { event.preventDefault(); this.selectCategory(category); }
      else if (event.target.closest('[data-retry]')) this.selectCategory(this.active, true);
      else if (event.target.closest('[data-prev]')) this.move(-1);
      else if (event.target.closest('[data-next]')) this.move(1);
      else {
        const dot = event.target.closest('[data-page]');
        if (dot) this.scrollToPage(Number(dot.dataset.page));
      }
    }

    handleKey(event) {
      if (event.target.closest('kj-collection-atlas') !== this) return;
      const tab = event.target.closest('[data-parent], [data-category]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End', ' '].includes(event.key)) return;
      event.preventDefault();
      if (event.key === ' ') { tab.click(); return; }
      const list = tab.hasAttribute('data-parent') ? this.parents : [...tab.closest('[data-group]').querySelectorAll('[data-category]')];
      const index = list.indexOf(tab);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? list.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + list.length) % list.length;
      list[next]?.focus();
      list[next]?.click();
    }

    showParent(parent) {
      if (!parent) return;
      const id = parent.dataset.parent;
      this.dataset.activeParent = id;
      this.parents.forEach(tab => {
        const selected = tab === parent;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.groups.forEach(group => { group.hidden = group.dataset.group !== id; });
      this.querySelector('.ka-panel').dataset.palette = parent.dataset.palette || 'inherit';
      this.querySelector('.ka-header [data-heading]').textContent = parent.dataset.heading;
      const subtitle = this.querySelector('.ka-header [data-subtitle]');
      subtitle.textContent = parent.dataset.subtitle;
      subtitle.hidden = !parent.dataset.subtitle;
      this.revealTab(parent);
    }

    selectParent(parent) {
      if (this.progressive) {
        const controller = this.ensureParent(parent.dataset.parent);
        if (controller) {
          this.parents.forEach(tab => {
            tab.classList.toggle('is-active', tab === parent);
            tab.setAttribute('aria-current', String(tab === parent));
          });
          controller.querySelector('.ka-panel').scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
        return;
      }
      if (parent.dataset.parent === this.dataset.activeParent && this.active) return;
      this.showParent(parent);
      const first = this.groups.find(group => group.dataset.group === parent.dataset.parent)?.querySelector('[data-category]');
      if (first) this.selectCategory(first);
      else {
        // An unconfigured editor group must not retain another parent's products.
        this.pending?.abort();
        this.requestNumber++;
        this.setLoading(false);
        this.active = null;
        this.results.hidden = true;
        this.error.hidden = true;
        this.querySelector('.ka-toolbar [data-product-heading]').textContent = 'Choose a collection in this group';
        this.querySelector('[data-controls]').hidden = true;
      }
    }

    async selectCategory(tab, retry = false) {
      if (!tab) return;
      if (tab === this.active && !retry && this.error.hidden && !this.results.hidden) return;
      this.pending?.abort();
      clearTimeout(this.timeout);
      const request = ++this.requestNumber;
      this.active = tab;
      this.dataset.activeChild = tab.dataset.category;
      this.categories.forEach(item => {
        if (item.closest('kj-collection-atlas') !== this) return;
        item.classList.toggle('is-active', item === tab);
        item.setAttribute('aria-current', item === tab ? 'true' : 'false');
      });
      this.querySelector('.ka-toolbar [data-product-heading]').textContent = tab.dataset.productHeading;
      this.querySelector('[data-error-link]').href = tab.href;
      this.error.hidden = true;
      this.results.hidden = false;
      this.revealTab(tab);
      if (this.cache.has(tab.href)) {
        this.renderProducts(this.cache.get(tab.href));
        return;
      }
      this.setLoading(true);
      this.status.textContent = `Loading ${tab.dataset.label}…`;
      this.pending = new AbortController();
      const controller = this.pending;
      this.timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const url = new URL(tab.href);
        if (url.origin !== location.origin) throw new Error('Unexpected collection origin');
        url.searchParams.set('section_id', 'kj-atlas-products');
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection request failed');
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const markup = this.cleanMarkup(doc);
        if (!markup) throw new Error('Missing product section');
        if (request !== this.requestNumber || !this.isConnected) return;
        this.cache.set(tab.href, markup);
        this.renderProducts(markup);
      } catch (error) {
        if (request !== this.requestNumber || !this.isConnected) return;
        this.setLoading(false);
        this.results.hidden = true;
        this.error.hidden = false;
        this.status.textContent = 'Products could not be loaded. Retry or open the collection.';
        this.querySelector('[data-controls]').hidden = true;
      } finally {
        if (request === this.requestNumber) clearTimeout(this.timeout);
      }
    }

    setLoading(loading) {
      this.results.classList.toggle('is-loading', loading);
      this.results.setAttribute('aria-busy', String(loading));
      this.results.inert = loading;
      this.querySelector('[data-controls]').hidden = loading;
      this.results.querySelector('.ka-skeleton')?.remove();
      if (loading) {
        const skeleton = document.createElement('div');
        skeleton.className = 'ka-skeleton';
        skeleton.setAttribute('aria-hidden', 'true');
        for (let i = 0; i < 4; i++) skeleton.append(document.createElement('span'));
        this.results.append(skeleton);
      }
    }

    renderProducts(markup) {
      // Markup comes only from our same-origin Liquid endpoint, never user input.
      this.results.innerHTML = markup;
      this.setLoading(false);
      this.bindProducts();
      this.results.style.minHeight = '';
      this.status.textContent = `${this.active.dataset.label} loaded. ${this.track?.children.length || 0} products.`;
    }

    bindProducts() {
      this.track = this.results.querySelector('[data-track]');
      if (this.track) this.track.scrollLeft = 0;
      const footer = this.results.querySelector('[data-footer-label]');
      if (footer && this.active) footer.textContent = `View all ${this.active.dataset.label}`;
      this.initWishlist();
      this.updateCarousel();
    }

    metrics() {
      if (!this.track?.firstElementChild) return { pages: 0, page: 0, step: 0, max: 0 };
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const card = this.track.firstElementChild.getBoundingClientRect().width;
      const visible = Math.max(1, Math.round((this.track.clientWidth + gap) / (card + gap)));
      const step = visible * (card + gap);
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const pages = Math.ceil(this.track.children.length / visible);
      const page = max > 1 && this.track.scrollLeft >= max - 2 ? pages - 1 : Math.min(pages - 1, Math.round(this.track.scrollLeft / step));
      return { pages, page, step, max };
    }

    updateCarousel() {
      if (!this.results || this.results.hidden || this.results.classList.contains('is-loading')) return;
      const { pages, page, max } = this.metrics();
      this.querySelector('[data-controls]').hidden = max < 2;
      this.querySelector('[data-prev]').disabled = page <= 0;
      this.querySelector('[data-next]').disabled = page >= pages - 1;
      const dots = this.results.querySelector('[data-dots]');
      if (!dots) return;
      const count = pages > 1 ? pages : 0;
      if (dots.children.length !== count) {
        dots.replaceChildren();
        for (let i = 0; i < count; i++) {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.page = i;
          button.setAttribute('aria-label', `Show product page ${i + 1}`);
          dots.append(button);
        }
      }
      [...dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === page)));
    }

    move(direction) { this.scrollToPage(this.metrics().page + direction); }
    scrollToPage(page) {
      if (!this.track) return;
      const { step, max } = this.metrics();
      this.track.scrollTo({ left: Math.min(max, Math.max(0, page * step)), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    revealTab(tab) {
      const row = tab.parentElement;
      const item = tab.getBoundingClientRect();
      const box = row.getBoundingClientRect();
      if (item.left < box.left) row.scrollLeft -= box.left - item.left + 8;
      else if (item.right > box.right) row.scrollLeft += item.right - box.right + 8;
    }

    startProgressive() {
      this.progressive = true;
      this.classList.add('ka--progressive');
      this.progressParents = this.parents.filter(parent => this.groups.some(group => group.dataset.group === parent.dataset.parent && group.querySelector('[data-category]')));
      this.firstParent = this.dataset.activeParent;
      this.controllers = new Map([[this.firstParent, this]]);
      this.originalPanel = this.querySelector('.ka-panel');
      this.querySelector('[data-parents]').removeAttribute('role');
      this.parents.forEach(parent => {
        parent.removeAttribute('role');
        parent.removeAttribute('aria-selected');
        parent.tabIndex = 0;
        parent.setAttribute('aria-current', String(parent.dataset.parent === this.firstParent));
      });
      // One sentinel watches the end of the currently appended chain, not page scroll events.
      this.sentinel = document.createElement('div');
      this.sentinel.className = 'ka-next-parent';
      this.sentinel.setAttribute('aria-hidden', 'true');
      this.originalPanel.after(this.sentinel);
      if ('IntersectionObserver' in window) {
        this.parentObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.appendNextParent();
        }, { rootMargin: '0px 0px 180px 0px' });
        this.parentObserver.observe(this.sentinel);
      }
      if (this.progressParents.length < 2) this.sentinel.hidden = true;
    }

    appendNextParent() {
      if (!this.progressive) return;
      const next = this.progressParents.find(parent => !this.controllers.has(parent.dataset.parent));
      if (next) this.ensureParent(next.dataset.parent);
      if (this.controllers.size === this.progressParents.length) {
        this.parentObserver?.disconnect();
        this.sentinel.hidden = true;
      }
    }

    ensureParent(id) {
      if (this.controllers.has(id)) return this.controllers.get(id);
      const target = this.progressParents.findIndex(parent => parent.dataset.parent === id);
      if (target < 0) return null;
      // A direct tab click also preserves the configured order of preceding parents.
      for (let i = 0; i <= target; i++) {
        const parent = this.progressParents[i];
        if (this.controllers.has(parent.dataset.parent)) continue;
        const group = this.groups.find(item => item.dataset.group === parent.dataset.parent);
        const child = document.createElement('kj-collection-atlas');
        child.className = 'ka ka-continuation';
        child.dataset.sectionId = `${this.dataset.sectionId}-${parent.dataset.parent}`;
        child.id = `KA-${child.dataset.sectionId}`;
        parent.setAttribute('aria-controls', child.id);
        child.dataset.activeParent = parent.dataset.parent;
        child.dataset.wishlist = this.dataset.wishlist;
        child.sharedCache = this.cache;
        const nav = document.createElement('nav');
        nav.dataset.parents = '';
        nav.hidden = true;
        const link = parent.cloneNode(true);
        link.id = `KAParent-${child.dataset.sectionId}-${parent.dataset.parent}`;
        link.removeAttribute('data-shopify-editor-block');
        nav.append(link);
        const panel = document.createElement('div');
        panel.className = 'ka-panel';
        panel.dataset.palette = parent.dataset.palette || 'inherit';
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-label', parent.dataset.heading);
        const header = this.originalPanel.querySelector('.ka-header').cloneNode(true);
        header.querySelector('[data-heading]').textContent = parent.dataset.heading;
        header.querySelector('[data-subtitle]').textContent = parent.dataset.subtitle;
        header.querySelector('[data-subtitle]').hidden = !parent.dataset.subtitle;
        // Move the category controls rather than cloning their editor attributes / IDs.
        group.hidden = false;
        group.id = `KAGroup-${child.dataset.sectionId}-${parent.dataset.parent}`;
        const shop = document.createElement('div');
        shop.className = 'ka-shop';
        shop.append(this.originalPanel.querySelector('.ka-toolbar').cloneNode(true));
        const status = document.createElement('p');
        status.className = 'ka-status';
        status.dataset.status = '';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        const error = this.error.cloneNode(true);
        error.hidden = true;
        const results = document.createElement('div');
        results.className = 'ka-results';
        results.dataset.results = '';
        results.id = `KAResults-${child.dataset.sectionId}`;
        // Reserve the existing panel's product height while the next collection loads.
        results.style.minHeight = `${Math.max(260, this.results.getBoundingClientRect().height)}px`;
        shop.append(status, error, results);
        panel.append(header, group, shop);
        child.append(nav, panel);
        this.controllers.set(parent.dataset.parent, child);
        this.sentinel.before(child);
        child.selectCategory(group.querySelector('[data-category]'));
      }
      if (this.controllers.size === this.progressParents.length) {
        this.parentObserver?.disconnect();
        this.sentinel.hidden = true;
      }
      return this.controllers.get(id);
    }

    stopProgressive() {
      this.parentObserver?.disconnect();
      if (!this.progressive) return;
      this.progressive = false;
      for (const [id, child] of this.controllers) {
        if (child === this) continue;
        const group = child.querySelector('[data-group]');
        if (group) {
          group.hidden = true;
          group.id = `KAGroup-${this.dataset.sectionId}-${id}`;
          this.originalPanel.querySelector('.ka-shop').before(group);
        }
        child.remove();
      }
      this.dataset.activeParent = this.firstParent;
      this.sentinel?.remove();
      this.controllers.clear();
      this.classList.remove('ka--progressive');
    }

    initWishlist() {
      const sdk = window.WishListHero_SDK;
      if (this.dataset.wishlist !== 'true' || !sdk?.InitializeAddToWishListButton) return;
      this.results.querySelectorAll('.ka-wishlist-button').forEach(el => {
        if (el.dataset.kaWishlist || el.querySelector('button')) return;
        const key = `ka-wish-${++wishlistSequence}`;
        el.classList.add(key);
        el.dataset.kaWishlist = 'true';
        const d = el.dataset;
        try {
          Promise.resolve(sdk.InitializeAddToWishListButton({ ButtonClassElement: `.${key}`, ProductId: d.wlhId, ProductVariantId: d.wlhVariantid, ProductLink: d.wlhLink, ProductTitle: d.wlhName, ProductPrice: Number(d.wlhPrice), ProductImage: d.wlhImage })).catch(() => { delete el.dataset.kaWishlist; });
        } catch (_) { delete el.dataset.kaWishlist; }
      });
    }
  }
  customElements.define('kj-collection-atlas', CollectionAtlas);
})();
